import { ColumnDef } from "@tanstack/react-table";
import { Path, useNavigate } from "@/router";
import { actionId, type TableRowMenu } from "@/hooks/useTableRowMenu";
import { buildActionColumn } from "@/components/Table";
import Icon from "@/components/Icon";
import { ActivityTableRow } from "./types";
import { CreatesEventType, SectionAvailability } from "../types";
import { MANAGING_SECTION_COLUMN_HEADER } from "./participantTableColumns";

export interface ActivityTableColumnsOptions {
  navigate: ReturnType<typeof useNavigate>;
  activityTypeId: string;
  setMenu: (menu: TableRowMenu) => void;
  createsEvent: CreatesEventType;
  canAddParticipants: (row: ActivityTableRow) => boolean;
  sectionAvailability?: SectionAvailability;
}

export function getActivityTableColumns({
  navigate,
  activityTypeId,
  setMenu,
  createsEvent,
  canAddParticipants,
  sectionAvailability
}: ActivityTableColumnsOptions): ColumnDef<ActivityTableRow, unknown>[] {
  return [
    {
      accessorKey: "title",
      header: "Title",
      cell: ({ row }) => (
        <div>
          <div>{row.original.title}</div>
        </div>
      ),
      size: 250,
      enableSorting: false,
      meta: { headerClassName: "hide-header-in-column" }
    },
    ...(createsEvent === CreatesEventType.OPTIONAL
      ? [
          {
            accessorKey: "eventName",
            header: "Associated Event",
            cell: ({ row }: { row: { original: ActivityTableRow } }) => {
              const hasEvent = !!row.original.eventSlug;
              const eventName = row.original.eventName;

              if (!hasEvent || eventName === "No") {
                return <span className="text-sys-brand-on-surface-variant">No</span>;
              }

              if (eventName === "Yes") {
                return (
                  <div
                    className="flex items-center gap-1 cursor-pointer hover:underline w-fit"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/activities/events/${row.original.eventSlug}/overview` as Path);
                    }}
                  >
                    <span>Yes</span>
                    <Icon name="navigate_next" className="text-sys-brand-on-surface-variant" />
                  </div>
                );
              }

              return <span>{eventName}</span>;
            },
            size: 250,
            enableSorting: false
          }
        ]
      : []),
    ...(createsEvent !== CreatesEventType.NEVER
      ? [
          {
            accessorKey: "location",
            header: "Location",
            cell: ({ row }: { row: { original: ActivityTableRow } }) => row.original.location,
            size: 300,
            enableSorting: false
          }
        ]
      : []),
    {
      accessorKey: "participants",
      header: "Participants",
      cell: ({ row }: { row: { original: ActivityTableRow } }) => row.original.participants,
      size: 120,
      enableSorting: false
    },
    {
      accessorKey: "startDate",
      header: "Start Date",
      size: 120,
      enableSorting: false
    },
    {
      accessorKey: "endDate",
      header: "End Date",
      size: 120,
      enableSorting: false
    },
    ...(sectionAvailability === SectionAvailability.CAN_CREATE
      ? [
          {
            accessorKey: "section",
            header: MANAGING_SECTION_COLUMN_HEADER,
            cell: ({ row }: { row: { original: ActivityTableRow } }) => row.original.section ?? null,
            size: 200,
            enableSorting: false
          }
        ]
      : []),
    buildActionColumn<ActivityTableRow>({
      getActions: (row) => {
        const hasEvent = !!row.eventSlug;
        return hasEvent
          ? [
              {
                text: "View Event",
                onClick: () => navigate(`/activities/events/${row.eventSlug}/overview` as Path)
              }
            ]
          : [
              {
                text: "View Activity",
                onClick: () =>
                  navigate(`/activities/activity-types/${activityTypeId}/activity/${row.id}` as Path, {
                    state: { activity: row }
                  })
              },
              ...(canAddParticipants(row)
                ? [
                    {
                      text: "Add Participants",
                      onClick: () =>
                        navigate(
                          `/activities/activity-types/${activityTypeId}/activity/${row.id}/add-participants` as Path
                        )
                    }
                  ]
                : [])
            ];
      },
      actionId,
      setMenu
    })
  ];
}
